import { useLayoutResults, useLayoutId, useProjectId } from '@consigli/hooks';
import { LayoutOptimizationResult, File as ConsigliFile } from '@consigli/types';
import { createContext, useContext, useState, ReactNode, useMemo } from 'react';

interface ResultsContextType {
  results: LayoutOptimizationResult[];
  isLoading: boolean;
  projectId: number;
  layoutId: string;
  selectedItem: LayoutOptimizationResult | null;
  setSelectedItem: (item: LayoutOptimizationResult | null) => void;
  isDetailViewOpen: boolean;
  setIsDetailViewOpen: (open: boolean) => void;
  deleteItem: LayoutOptimizationResult | null;
  setDeleteItem: (item: LayoutOptimizationResult | null) => void;
  editItem: LayoutOptimizationResult | null;
  setEditItem: (item: LayoutOptimizationResult | null) => void;
  // File viewer state
  previewFile: ConsigliFile | null;
  setPreviewFile: (file: ConsigliFile | null) => void;
  previewResultId: string | null;
  setPreviewResultId: (id: string | null) => void;
}

const ResultsContext = createContext<ResultsContextType | undefined>(undefined);

interface ResultsProviderProps {
  children: ReactNode;
}

export const ResultsProvider = ({ children }: ResultsProviderProps) => {
  const projectId = useProjectId();
  const layoutId = useLayoutId();

  const { results, isLoading } = useLayoutResults({
    projectId,
    layoutId,
    page: 'all',
  });

  const [selectedItem, setSelectedItem] = useState<LayoutOptimizationResult | null>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<LayoutOptimizationResult | null>(null);
  const [editItem, setEditItem] = useState<LayoutOptimizationResult | null>(null);
  const [previewFile, setPreviewFile] = useState<ConsigliFile | null>(null);
  const [previewResultId, setPreviewResultId] = useState<string | null>(null);

  const value = useMemo(
    () => ({
      results: results || [],
      isLoading,
      projectId,
      layoutId,
      selectedItem,
      setSelectedItem,
      isDetailViewOpen,
      setIsDetailViewOpen,
      deleteItem,
      setDeleteItem,
      editItem,
      setEditItem,
      previewFile,
      setPreviewFile,
      previewResultId,
      setPreviewResultId,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      results,
      isLoading,
      projectId,
      layoutId,
      selectedItem,
      isDetailViewOpen,
      deleteItem,
      editItem,
      previewFile,
      previewResultId,
    ],
  );

  return <ResultsContext.Provider value={value}>{children}</ResultsContext.Provider>;
};

export const useResultsContext = () => {
  const context = useContext(ResultsContext);
  if (context === undefined) {
    throw new Error('useResultsContext must be used within a ResultsProvider');
  }
  return context;
};
